import React, { useState, useEffect, useCallback } from 'react';
import { api } from './api';
import Icon from './Icon';

// Rough SMS segment estimate (GSM-7: 160 chars, then 153/segment).
const segments = (len) => (len === 0 ? 0 : len <= 160 ? 1 : Math.ceil(len / 153));
const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

// Ready-to-use US-holiday promo templates ({first_name} is personalized).
const TEMPLATES = [
  { label: 'New Year', text: 'Happy New Year, {first_name}! Start the year with a spotless ride — 15% off any detail through January. Book at jgdetailingllc.com' },
  { label: "Valentine's", text: '{first_name}, surprise someone (or yourself) this Valentine’s Day with a fresh, clean car. Book a detail at jgdetailingllc.com' },
  { label: "Presidents' Day", text: 'Presidents’ Day deal, {first_name}! Save 20% on full detailing this weekend only. Reserve your spot at jgdetailingllc.com' },
  { label: 'Spring Cleaning', text: 'Spring is here, {first_name}! Wash away winter with a deep interior detail — 15% off this month. Book at jgdetailingllc.com' },
  { label: 'Memorial Day', text: 'Memorial Day savings, {first_name}! Kick off summer with 20% off any detail this weekend. Book early at jgdetailingllc.com' },
  { label: 'July 4th', text: 'Happy 4th, {first_name}! Make your ride shine for the holiday — 15% off all details through July 4th. jgdetailingllc.com' },
  { label: 'Labor Day', text: 'Labor Day special, {first_name}! Relax while we do the work — 20% off detailing this weekend. Book at jgdetailingllc.com' },
  { label: 'Halloween', text: 'No tricks, just a treat, {first_name}! 15% off any detail this Halloween week. Grab a slot at jgdetailingllc.com' },
  { label: 'Thanksgiving', text: 'Happy Thanksgiving, {first_name}! Grateful for your business — enjoy 15% off your next detail. Book at jgdetailingllc.com' },
  { label: 'Black Friday', text: 'Black Friday, {first_name}! Our biggest deal of the year — 25% off any detail package. Limited spots at jgdetailingllc.com' },
  { label: 'Christmas', text: 'Happy Holidays, {first_name}! Give the gift of a spotless car — detail gift cards available. Book at jgdetailingllc.com' },
  { label: 'Winter Protect', text: 'Winter salt is rough on your car, {first_name}. Protect it with a wash & wax — 15% off this month. jgdetailingllc.com' },
  { label: 'Win-back', text: 'Hi {first_name}, it’s been a while! Book your next detail and save 10% — jgdetailingllc.com' },
];

const TOPUP_PRESETS = [2500, 5000, 10000];

const MarketingPanel = ({ showPricing = false }) => {
  const [audience, setAudience] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [wallet, setWallet] = useState(null); // { balance_cents, rate_cents }
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [topupBusy, setTopupBusy] = useState(false);
  const [customAmt, setCustomAmt] = useState('');
  const [rateInput, setRateInput] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      const [a, c, w] = await Promise.all([api.marketingAudience(), api.marketingCampaigns(), api.billing()]);
      setAudience(a.count);
      setCampaigns(c);
      setWallet(w);
      setRateInput(((w.rate_cents || 0) / 100).toFixed(2));
      setError('');
    } catch (err) { setError(err.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Show a notice after returning from Stripe Checkout.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get('topup');
    if (p === 'success') {
      setNotice('Payment received — credit will appear in a moment.');
      setTimeout(load, 2500);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (p === 'cancel') {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [load]);

  const optOutNote = /\bSTOP\b/i.test(message) ? '' : ' Reply STOP to opt out.';
  const preview = (message || '').replace(/\{first_?name\}/gi, 'John') + (message ? optOutNote : '');
  const len = preview.length;
  const segs = segments((message || '').replace(/\{first_?name\}/gi, '').length + optOutNote.length);
  const rate = wallet?.rate_cents ?? 0;
  const estCost = (audience || 0) * segs * rate;
  const insufficient = wallet && estCost > wallet.balance_cents;

  const send = async () => {
    setError(''); setResult(null);
    if (!message.trim()) { setError('Write a message first.'); return; }
    if (!window.confirm(`Send to ${audience} customer${audience === 1 ? '' : 's'} for ${money(estCost)}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await api.marketingSend(message.trim());
      setResult(r.message);
      setMessage('');
      load();
    } catch (err) { setError(err.message); load(); }
    finally { setBusy(false); }
  };

  const topUp = async (amountCents) => {
    setTopupBusy(true); setError('');
    try {
      const { url } = await api.billingTopup(amountCents, window.location.pathname);
      window.location.href = url; // redirect to Stripe Checkout
    } catch (err) { setError(err.message); setTopupBusy(false); }
  };

  const saveRate = async () => {
    const cents = Math.round(parseFloat(rateInput) * 100);
    if (!Number.isFinite(cents) || cents < 0) { setError('Invalid rate.'); return; }
    try { setWallet(await api.billingSetRate(cents)); setNotice('Rate updated.'); setTimeout(() => setNotice(''), 2000); }
    catch (err) { setError(err.message); }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');

  return (
    <div className="marketing-panel">
      {/* Wallet bar */}
      <div className="wallet-bar">
        <div className="wallet-balance">
          <span className="wallet-label">SMS credit</span>
          <span className="wallet-amount">{wallet ? money(wallet.balance_cents) : '…'}</span>
        </div>
        <div className="wallet-topup">
          {TOPUP_PRESETS.map((amt) => (
            <button key={amt} className="admin-ghost-btn" disabled={topupBusy} onClick={() => topUp(amt)}>{money(amt)}</button>
          ))}
          <div className="wallet-custom">
            <span>$</span>
            <input type="number" min="5" placeholder="custom" value={customAmt} onChange={(e) => setCustomAmt(e.target.value)} />
            <button className="admin-primary-btn" disabled={topupBusy || !customAmt} onClick={() => topUp(Math.round(parseFloat(customAmt) * 100))}>
              <Icon name="plus" size={14} /> Add credit
            </button>
          </div>
        </div>
      </div>
      {notice && <p className="deploy-ok">{notice}</p>}

      <div className="mkt-grid">
        <div className="mkt-compose">
          <div className="mkt-head">
            <h3><Icon name="message" size={18} /> New SMS campaign</h3>
            <span className="mkt-audience"><Icon name="users" size={15} /> {audience == null ? '…' : audience} recipient{audience === 1 ? '' : 's'}</span>
          </div>

          <div className="mkt-templates">
            <span className="mkt-templates-label">Holiday templates</span>
            <div className="mkt-template-chips">
              {TEMPLATES.map((t) => (
                <button key={t.label} type="button" className="mkt-chip" onClick={() => setMessage(t.text)}>{t.label}</button>
              ))}
            </div>
          </div>

          <textarea
            className="mkt-textarea"
            rows={5}
            placeholder="Hi {first_name}, this week only — 20% off any detail! Book at jgdetailingllc.com"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <div className="mkt-meta">
            <span>Use <code>{'{first_name}'}</code> to personalize.</span>
            <span>{len} chars · {segs || 0} SMS segment{segs === 1 ? '' : 's'}</span>
          </div>

          {message && (
            <div className="mkt-preview">
              <span className="mkt-preview-label">Preview</span>
              <p>{preview}</p>
            </div>
          )}

          {message && audience > 0 && (
            <div className={`mkt-cost ${insufficient ? 'short' : ''}`}>
              <span>Estimated cost</span>
              <strong>{money(estCost)}</strong>
              <small>{audience} × {segs} seg × {money(rate)}</small>
            </div>
          )}

          <p className="mkt-note">
            <Icon name="alert-triangle" size={14} /> A “Reply STOP to opt out” line is added automatically.
            Only send to customers who agreed to marketing messages.
          </p>

          {error && <p className="admin-error"><Icon name="alert" size={15} /> {error}</p>}
          {result && <p className="deploy-ok">{result}</p>}

          <button className="admin-primary-btn" onClick={send} disabled={busy || !message.trim() || !audience || insufficient}>
            {busy ? 'Sending…' : insufficient
              ? <>Add credit to send ({money(estCost)})</>
              : <><Icon name="message" size={15} /> Send to {audience ?? 0} for {money(estCost)}</>}
          </button>
        </div>

        <div className="mkt-history">
          {showPricing && (
            <div className="rate-card">
              <h3>Pricing (Zyrix)</h3>
              <p className="admin-subtle">Price charged to the client per SMS segment. Your Twilio cost is ~$0.011.</p>
              <div className="rate-row">
                <span>$</span>
                <input type="number" step="0.01" min="0" value={rateInput} onChange={(e) => setRateInput(e.target.value)} />
                <span>/ segment</span>
                <button className="admin-ghost-btn" onClick={saveRate}>Save</button>
              </div>
            </div>
          )}

          <h3>Recent campaigns</h3>
          <div className="table-wrap">
            <table className="bookings-table">
              <thead><tr><th>Sent</th><th>Message</th><th>Delivered</th></tr></thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id}>
                    <td>{fmt(c.created_at)}</td>
                    <td className="mkt-msg-cell">{c.message}</td>
                    <td>{c.sent}/{c.recipients}{c.failed ? ` (${c.failed} failed)` : ''}</td>
                  </tr>
                ))}
                {!campaigns.length && <tr><td colSpan={3} className="empty-row">No campaigns yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingPanel;
